import { useCallback, useEffect, useState } from "react";
import { api, errMsg, STATUS_META, MONTHS_IT } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PaperPlaneTilt, FirstAidKit, FilePdf } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 3 + i);

export default function MyArea() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/me/attendance", { params: { year, month } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;

  return (
    <div data-testid="my-area-page" className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">La mia area</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data?.employee ? `${data.employee.last_name} ${data.employee.first_name} · ${data.employee.code} · ${data.employee.department}` : ""}
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3" data-testid="my-shortcuts">
        {[["/richieste", "Le mie richieste", "Ferie, riposo, permessi 104 e malattia", PaperPlaneTilt, "my-requests-btn"],
          ["/malattia", "Malattia", "Comunica i giorni e invia il certificato", FirstAidKit, "my-sickness-btn"],
          ["/miei-documenti", "I miei documenti", "Buste paga, HACCP, corsi e contratti", FilePdf, "my-documents-btn"]]
          .map(([to, titolo, testo, Icona, tid]) => (
            <Link key={to} to={to} data-testid={tid}
              className="rounded-sm border border-border bg-card p-4 hover:border-primary hover:bg-secondary/40 transition-colors duration-150">
              <div className="flex items-center gap-2">
                <Icona size={18} className="text-primary" />
                <h2 className="font-head font-semibold text-base">{titolo}</h2>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">{testo}</p>
            </Link>
          ))}
      </div>

      <div className="rounded-sm border border-border bg-card">
        <div className="mobile-stack flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <h2 className="font-head font-semibold text-base">Riepilogo mese</h2>
          <div className="ml-auto flex gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-[130px] rounded-sm" data-testid="my-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-[100px] rounded-sm" data-testid="my-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="p-4 grid grid-cols-3 sm:grid-cols-3 gap-2">
          {Object.keys(STATUS_META).map((k) => (
            <div key={k} className="rounded-sm border border-border p-2.5" data-testid={`my-${k}`}>
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className={`h-2 w-2 rounded-full ${STATUS_META[k].dot}`} /> {k}
              </div>
              <div className="text-xl font-head font-bold tabular-nums mt-1">{s?.[k] ?? "—"}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border overflow-x-auto scroll-thin">
          <table className="w-full text-sm" data-testid="my-days-table">
            <thead className="bg-secondary">
              <tr>{["Giorno", "Causale", "Note"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {data && Array.from({ length: data.days || 0 }, (_, i) => i + 1).map((d) => {
                const iso = `${data.year}-${String(data.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const r = (data.records || []).find((x) => x.date === iso);
                const dt = new Date(data.year, data.month - 1, d);
                const weekend = [0, 6].includes(dt.getDay());
                return (
                  <tr key={iso} className={`border-t border-border ${weekend ? "bg-muted/40" : ""}`} data-testid={`my-day-${d}`}>
                    <td className="px-3 py-1 tabular-nums text-xs">
                      {String(d).padStart(2, "0")}{" "}
                      <span className="text-muted-foreground">{dt.toLocaleDateString("it-IT", { weekday: "short" })}</span>
                    </td>
                    <td className="px-3 py-1">
                      {r
                        ? <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${STATUS_META[r.status]?.cell}`}>{r.status}</span>
                        : <span className="text-[10px] font-semibold text-muted-foreground">NON REGISTRATO</span>}
                    </td>
                    <td className="px-3 py-1 text-xs text-muted-foreground">{r?.notes || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
